/** Мелкие общие утилиты без зависимостей (без i18n/React). */

/** Извлекает читаемое сообщение из ошибки (включая ApiError с .status). */
export function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return typeof e === 'string' ? e : 'Неизвестная ошибка';
}

/** Длина игрового кода (PIN). Совпадает с серверной генерацией (6 цифр). */
export const PIN_LENGTH = 6;

/**
 * Ключ перевода для кода WS-ошибки (событие `error`), если код известен/локализуем;
 * иначе null — caller показывает сырое `message`. И5: nick_locked/duplicate_nick
 * приходят с серверным английским сообщением, поэтому маппим их в i18n-ключи.
 * Фаза 13: join_closed (lockAfterStart) — вход новым ником после старта закрыт.
 * nick_taken/superseded обрабатываются экранами (A2b/B1), здесь их нет.
 */
export function wsErrorKey(code: string): string | null {
  switch (code) {
    case 'nick_locked':
      return 'rename.errLocked';
    case 'duplicate_nick':
      return 'rename.errDuplicate';
    case 'join_closed':
      return 'join.errors.joinClosed';
    default:
      return null;
  }
}

/**
 * Единицы размера по умолчанию (двоичные, 1024): русские сокращения. Caller может
 * передать локализованный массив (напр. из i18n `common.bytes.*`), чтобы вывод был
 * язык-корректен; для ru дефолт совпадает с каталогом, а юнит-тест не зависит от i18n.
 */
export const DEFAULT_BYTE_UNITS = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'] as const;

/**
 * Форматирует размер в байтах в человекочитаемый вид (Б/КБ/МБ/ГБ/ТБ, основание 1024).
 * Используется в admin-дашборде игр (Фаза 7, И3, план §2.6): payloadBytes/imagesBytes/
 * totalBytes показываются компактно. 0/отрицательное/NaN → «0 <unit>»; для байт —
 * целое, для старших единиц — 1–2 значащих знака без висящих нулей (через Number()
 * «1.50»→1.5, «1.00»→1). units ограничен длиной массива (защита от переполнения степени).
 */
export function formatBytes(n: number, units: readonly string[] = DEFAULT_BYTE_UNITS): string {
  if (!Number.isFinite(n) || n <= 0) {
    return `0 ${units[0] ?? 'B'}`;
  }
  const degree = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  if (degree <= 0) {
    return `${Math.round(n)} ${units[0]}`;
  }
  const value = n / Math.pow(1024, degree);
  const fixed = value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2);
  // Number(...) убирает висящие нули: «1.50»→1.5, «12.0»→12, «1.00»→1.
  return `${Number(fixed)} ${units[degree]}`;
}

/** Размер страницы истории сессий (совпадает с серверным дефолтом limit=20; B4). */
export const HISTORY_PAGE_SIZE = 20;

/**
 * Локализованная человекочитаемая длительность сессии: «17 мин» / «45 с»
 * (§B4.1). t — функция перевода (передаётся параметром, чтобы модуль не зависел
 * от i18next); неполная минута не засчитывается (1063 с → «17 мин»).
 */
export function historyDuration(
  t: (key: string, opts?: Record<string, number>) => string,
  sec: number,
): string {
  if (sec < 60) {
    return t('dashboard.history.durationSec', { sec });
  }
  return t('dashboard.history.durationMin', { min: Math.floor(sec / 60) });
}
